/**
 * The brand, fetched once at boot.
 *
 * Deliberately not a build-time `VITE_` variable: Vite would inline the name into the bundle, so
 * renaming the product would mean rebuilding and redeploying the front end. One small cacheable
 * request keeps a rename to a config change. See PLAN.md section 1.2.
 */

import { api, type Schemas } from "../api/client";

/** Generated from the API schema - never a hand-written copy that could drift from the server. */
export type Branding = Schemas["BrandingResponse"];

/** Used only if the API cannot be reached, so the page still renders something sane. */
export const FALLBACK: Branding = {
  product_name: "",
  legal_name: "",
  legal_jurisdiction: "",
  tagline: "",
  primary_domain: "",
  support_email: "",
  primary_colour: "#3987e5",
  terms_version: "",
  logo_url: "/brand/logo.png",
  favicon_url: "/brand/favicon.png",
  backdrop_url: "/brand/backdrop.jpg",
  login_video_url: "/brand/login.mp4",
  login_poster_url: "/brand/login-poster.jpg",
};

export async function fetchBranding(signal?: AbortSignal): Promise<Branding> {
  const { data, response } = await api.GET("/api/v1/branding", { signal });
  if (!data) throw new Error(`branding: ${response.status}`);
  return data;
}

/** Push the brand into the places the DOM owns: the tab title, the favicon and the theme colour. */
export function applyBranding(brand: Branding): void {
  if (brand.product_name) document.title = brand.product_name;
  document.documentElement.style.setProperty("--brand-primary", brand.primary_colour);

  const icon = document.querySelector<HTMLLinkElement>("link[rel='icon']") ?? document.createElement("link");
  icon.rel = "icon";
  icon.href = brand.favicon_url;
  document.head.appendChild(icon);
}
